import React, { useEffect, useRef } from 'react'
import * as echarts from 'echarts'
import { Box } from '@mui/material'

interface SentimentDivergingBarProps {
  positive?: number;
  neutral?: number;
  negative?: number;
  sentimentIndexOverride?: number;
}

export const SentimentDivergingBar: React.FC<SentimentDivergingBarProps> = ({
  positive = 0.77,  // Default values from the actual Platinum data
  neutral = 0.26,
  negative = 0.14,
  sentimentIndexOverride,
}) => {
  const chartRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!chartRef.current) return

    // Get the actual container dimensions
    const containerWidth = chartRef.current.offsetWidth || 800
    const containerHeight = chartRef.current.offsetHeight || 500
    
    // Initialize with explicit dimensions
    const chart = echarts.init(chartRef.current, null, {
      width: containerWidth,
      height: containerHeight
    })
    
    // Convert to percentages - these should add up to roughly 100%
    const total = positive + neutral + negative
    const negPct = negative * 100
    const neuPct = neutral * 100
    const posPct = positive * 100
    
    console.log('Chart percentages:', { negPct, neuPct, posPct, total })
    
    // Calculate sentiment index
    // Use override if provided from data, otherwise calculate: (positive - negative) * 100
    const sentimentIndex = sentimentIndexOverride !== undefined ? 
      sentimentIndexOverride : 
      (positive - negative) * 100 // Simple difference as percentage

    const option = {
      title: {
        text: 'Sentiment Analysis',
        left: 'center',
        top: 10,
        textStyle: {
          fontSize: 18,
          fontWeight: 'bold'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: function(params: any) {
          return params[0].name + '<br/>' +
            params.map((p: any) => {
              return p.marker + ' ' + p.seriesName + ': ' + Math.abs(p.value) + '%'
            }).join('<br/>')
        }
      },
      legend: {
        data: ['Negative', 'Neutral', 'Positive'],
        bottom: 20
      },
      grid: {
        top: 60,
        bottom: 80,
        left: 100,
        right: 100,
        containLabel: true
      },
      xAxis: {
        type: 'value',
        axisLabel: {
          formatter: function(value: number) {
            return Math.abs(value) + '%'
          }
        },
        max: 100,
        min: -20
      },
      yAxis: {
        type: 'category',
        axisTick: {
          show: false
        },
        data: ['Sentiment']
      },
      series: [
        {
          name: 'Negative',
          type: 'bar',
          stack: 'total',
          label: {
            show: true,
            position: 'inside',
            formatter: function() {
              return `Negative: ${negPct.toFixed(0)}%`
            },
            color: '#fff',
            fontSize: 14
          },
          emphasis: {
            focus: 'series'
          },
          itemStyle: {
            color: '#e74c3c'
          },
          data: [-negPct]
        },
        {
          name: 'Neutral',
          type: 'bar',
          stack: 'total',
          label: {
            show: true,
            position: 'inside',
            formatter: function() {
              return `Neutral: ${neuPct.toFixed(0)}%`
            },
            color: '#fff',
            fontSize: 14
          },
          emphasis: {
            focus: 'series'
          },
          itemStyle: {
            color: '#95a5a6'
          },
          data: [neuPct]
        },
        {
          name: 'Positive',
          type: 'bar',
          stack: 'total',
          label: {
            show: true,
            position: 'inside',
            formatter: function() {
              return `Positive: ${posPct.toFixed(0)}%`
            },
            color: '#fff',
            fontSize: 14
          },
          emphasis: {
            focus: 'series'
          },
          itemStyle: {
            color: '#2ecc71'
          },
          data: [posPct]
        }
      ],
      graphic: [
        // Zero line
        {
          type: 'line',
          shape: {
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 1
          },
          left: 'center',
          top: 60,
          bottom: 80,
          style: {
            stroke: '#333',
            lineWidth: 2
          }
        },
        // Sentiment Index
        {
          type: 'text',
          right: 20,
          top: 20,
          style: {
            text: `Sentiment Index: ${sentimentIndex.toFixed(1)}`,
            fontSize: 14,
            fontWeight: 'bold',
            fill: sentimentIndex > 0 ? '#2ecc71' : '#e74c3c'
          }
        }
      ]
    }

    chart.setOption(option)

    const handleResize = () => {
      chart.resize()
    }

    window.addEventListener('resize', handleResize)
    
    // Add ResizeObserver for better size detection
    const resizeObserver = new ResizeObserver(() => {
      const newWidth = chartRef.current?.offsetWidth || 800
      const newHeight = chartRef.current?.offsetHeight || 500
      chart.resize({
        width: newWidth,
        height: newHeight
      })
    })
    
    resizeObserver.observe(chartRef.current)
    
    // Force resize after a short delay to ensure proper rendering
    setTimeout(() => {
      const width = chartRef.current?.offsetWidth || 800
      const height = chartRef.current?.offsetHeight || 500
      chart.resize({
        width: width,
        height: height
      })
    }, 100)

    return () => {
      window.removeEventListener('resize', handleResize)
      resizeObserver.disconnect()
      chart.dispose()
    }
  }, [positive, neutral, negative])

  return (
    <Box
      ref={chartRef}
      sx={{
        width: '100%',
        height: '500px',
        minHeight: '500px',
        display: 'block',
        position: 'relative'
      }}
    />
  )
}